import { Injectable } from "@angular/core";
import { take } from "rxjs";
import { IRestLayerRequest } from "@gtmhub/core";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { PluginStateService } from "@webapp/plugins/components/plugin/services/plugin-state.service";
import { PluginService } from "@webapp/plugins/components/plugin/services/plugin.service";
import { GetRequest, JiraIssue, PluginPayload } from "@webapp/plugins/plugins.models";
import { PluginsFacade } from "@webapp/plugins/services/plugins-facade.service";
import { mergeCustomFields, setParams } from "@webapp/plugins/utils";
import { Task } from "@webapp/tasks/models/tasks.models";
import { TasksFacade } from "@webapp/tasks/services/task-facade.service";

@Injectable()
export class PluginTasksService extends PluginService {
  constructor(
    private broadcastService: BroadcastService,
    private tasksFacade: TasksFacade,
    private pluginsFacade: PluginsFacade,
    pluginStateService: PluginStateService,
    destroy$: DestroyService
  ) {
    super(pluginStateService, destroy$);
  }

  public linkIssue(type: string, issue: JiraIssue): void {
    const assignee = issue.fields.assignee;
    const newTask = {
      description: issue.fields.description,
      externalId: issue.self,
      externalSystem: "jira",
      name: issue.fields.summary,
      parentId: this.item.id,
      priority: issue.fields.priority ? +issue.fields.priority.id : 0,
      status: issue.fields.status.name,
      taskType: issue.fields.issuetype.name,
      userEmail: assignee?.emailAddress,
      metadata: {
        "owner-email": assignee?.emailAddress,
        "owner-display-name": assignee?.displayName,
        "issue-key": issue.key,
      },
    };

    this.tasksFacade
      .create$(newTask, { pluginId: issue.pluginSettings.pluginId })
      .pipe(take(1))
      .subscribe((newTask) => {
        this.broadcastService.emit("taskCreated", { task: newTask, sessionId: this.item.sessionId });
        this.postMessage(newTask, "linkIssue");
      });
  }

  public getTask(type: string, data: GetRequest): void {
    this.tasksFacade
      .getTask$(data.id, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public getTasks(type: string, data: PluginPayload<IRestLayerRequest>): void {
    this.tasksFacade
      .getTasksV2$(
        {
          filter: data.payload.filter,
          fields: data.payload.fields.split(","),
          limit: data.payload.limit,
          page: data.payload.page,
          skip: data.payload.skip,
          sort: data.payload.sort.split(","),
        },
        setParams(data.pluginSettings.pluginId)
      )
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public createTask(type: string, data: PluginPayload<Task>): void {
    this.tasksFacade
      .create$(data.payload, { pluginId: this.plugin.pluginId })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
          this.broadcastService.emit("taskCreated", { response, sessionId: this.item.sessionId });
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }

  public updateTask(type: string, data: PluginPayload<Task>): void {
    this.tasksFacade
      .get$(data.payload.id)
      .pipe(take(1))
      .subscribe((task) => {
        const existingCustomFields = task.customFields;
        const newCustomFields = data.payload.customFields;

        /** ensure to merge both objects, otherwise it will omit the customfields not specified in palyoad */
        if (newCustomFields) {
          data.payload.customFields = mergeCustomFields(existingCustomFields, newCustomFields);
        }

        this.tasksFacade
          .update$(data.payload.id, data.payload, setParams(data.pluginSettings.pluginId))
          .pipe(take(1))
          .subscribe({
            next: (response) => {
              this.postMessage(response, type);
              this.broadcastService.emit("taskUpdated", { response, update: data.payload, sessionId: this.item.sessionId });
            },
            error: (error) => {
              this.postMessage(this.pluginsFacade.transformHttpError(error), type);
            },
          });
      });
  }

  public deleteTask(type: string, data: GetRequest): void {
    this.tasksFacade
      .deleteTask$(data.id, setParams(data.pluginSettings.pluginId))
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.postMessage(response, type);
          this.broadcastService.emit("taskDeleted", { task: { id: data.id }, sessionId: this.item.sessionId });
        },
        error: (error) => {
          this.postMessage(this.pluginsFacade.transformHttpError(error), type);
        },
      });
  }
}
